import React, { Component, ReactNode, ErrorInfo } from 'react';

import { handleReactError, LifeFinderError } from '../../../utils/errorHandling';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: LifeFinderError, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: LifeFinderError | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary Component
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI instead of crashing the app.
 *
 * This integrates with our centralized error handling system.
 *
 * @example
 * <ErrorBoundary fallback={<div>Something went wrong</div>}>
 *   <MyComponent />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Process error through our centralized error handling system
    const processedError = handleReactError(error, errorInfo);

    // Update state with processed error
    this.setState({
      error: processedError,
      errorInfo: errorInfo,
    });

    // Call optional error callback
    if (this.props.onError) {
      this.props.onError(processedError, errorInfo);
    }

    // Log for debugging
    console.error('ErrorBoundary caught an error:', {
      error,
      errorInfo,
      processedError,
    });
  }

  handleRetry = (): void => {
    // Reset error state to retry rendering
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleReload = (): void => {
    // Reload the entire page as last resort
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Custom fallback UI or use provided fallback
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div style={styles.container}>
          <div style={styles.content}>
            <div style={styles.icon}>⚠️</div>
            <h2 style={styles.title}>Något gick fel</h2>

            <p style={styles.message}>
              {this.state.error?.userMessage ||
                'Ett oväntat fel uppstod i applikationen. Du kan försöka igen eller ladda om sidan.'}
            </p>

            <div style={styles.actions}>
              <button style={styles.primaryButton} onClick={this.handleRetry}>
                Försök igen
              </button>

              <button style={styles.secondaryButton} onClick={this.handleReload}>
                Ladda om sidan
              </button>
            </div>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details style={styles.details}>
                <summary style={styles.summary}>Teknisk information (utveckling)</summary>
                <div style={styles.errorDetails}>
                  <p>
                    <strong>Fel:</strong> {this.state.error.message}
                  </p>
                  <p>
                    <strong>Typ:</strong> {this.state.error.type}
                  </p>
                  <p>
                    <strong>Severity:</strong> {this.state.error.severity}
                  </p>
                  <p>
                    <strong>Tid:</strong> {this.state.error.timestamp.toLocaleString()}
                  </p>

                  {this.state.errorInfo && (
                    <div>
                      <p>
                        <strong>Component Stack:</strong>
                      </p>
                      <pre style={styles.stackTrace}>{this.state.errorInfo.componentStack}</pre>
                    </div>
                  )}
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Styled components using CSS-in-JS approach consistent with the app
const styles = {
  container: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: '#f8f9fa',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },

  content: {
    backgroundColor: 'white',
    padding: '2rem',
    borderRadius: '8px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    textAlign: 'center' as const,
    maxWidth: '500px',
    margin: '1rem',
  },

  icon: {
    fontSize: '3rem',
    marginBottom: '1rem',
  },

  title: {
    color: '#e74c3c',
    marginBottom: '1rem',
    fontSize: '1.5rem',
  },

  message: {
    color: '#555',
    marginBottom: '2rem',
    lineHeight: '1.5',
  },

  actions: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'center',
    flexWrap: 'wrap' as const,
  },

  primaryButton: {
    backgroundColor: '#3498db',
    color: 'white',
    border: 'none',
    padding: '0.75rem 1.5rem',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: 'bold' as const,
  },

  secondaryButton: {
    backgroundColor: '#95a5a6',
    color: 'white',
    border: 'none',
    padding: '0.75rem 1.5rem',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
  },

  details: {
    marginTop: '2rem',
    textAlign: 'left' as const,
    fontSize: '0.9rem',
  },

  summary: {
    cursor: 'pointer',
    fontWeight: 'bold' as const,
    padding: '0.5rem',
    backgroundColor: '#ecf0f1',
    borderRadius: '4px',
  },

  errorDetails: {
    padding: '1rem',
    backgroundColor: '#fff5f5',
    borderRadius: '4px',
    marginTop: '0.5rem',
  },

  stackTrace: {
    backgroundColor: '#2c3e50',
    color: '#ecf0f1',
    padding: '1rem',
    borderRadius: '4px',
    fontSize: '0.8rem',
    overflow: 'auto',
    maxHeight: '200px',
  },
};

export default ErrorBoundary;
